import React, { useMemo, useState } from 'react'
import { Check, X } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { useEditorStore, type RowPositionKind } from '../../store/editorStore'
import { previewRowSlot } from '../../utils/rowSlots.logic'

const FIELD: React.CSSProperties = {
  background: 'var(--bg-panel, #111827)', border: '1px solid var(--border-color, #374151)',
  borderRadius: 6, color: 'var(--text-primary, #f3f4f6)', padding: '6px 10px', fontSize: 12,
  width: '100%', boxSizing: 'border-box',
}

export const RowSlotModal: React.FC<{ rowId: string; onClose: () => void }> = ({ rowId, onClose }) => {
  const { row, racks, dcSettings, applyRowSlot } = useEditorStore(useShallow(state => ({
    row: state.rows[rowId], racks: state.racks, dcSettings: state.dcSettings, applyRowSlot: state.applyRowSlot,
  })))
  const [position, setPosition] = useState(1)
  const [kind, setKind] = useState<Exclude<RowPositionKind, 'rack'>>('empty')
  const [label, setLabel] = useState('')
  const plan = useMemo(() => row ? previewRowSlot({
    row, racks: Object.values(racks).filter(rack => rack.rowId === rowId), position, kind, label,
    floorPlan: dcSettings.floorPlan, namingPattern: dcSettings.namingMask, module: dcSettings.module, hall: dcSettings.hall,
    namingProfiles: dcSettings.namingProfiles, namingAssignments: dcSettings.namingAssignments,
  }) : null, [row, racks, rowId, position, kind, label, dcSettings])

  if (!row || !plan) return null
  const apply = () => {
    if (!plan.canApply) return
    applyRowSlot({ row: plan.nextRow, additions: plan.additions })
    onClose()
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 430, maxWidth: '100%', background: '#0d1117', border: '1px solid #1e293b', borderRadius: 10, boxShadow: '0 24px 64px rgba(0,0,0,.8)' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><div style={{ fontSize: 15, fontWeight: 700, color: '#f3f4f6' }}>Место в «{row.name}»</div><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Позиция сохраняется даже без стойки</div></div>
          <button onClick={onClose} aria-label="Закрыть" style={{ background: 'none', border: 0, color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Позиция
            <input style={{ ...FIELD, marginTop: 4 }} type="number" min={1} value={position} onChange={event => setPosition(Math.max(1, Number(event.target.value) || 1))} />
          </label>
          <label style={{ fontSize: 11, color: '#94a3b8' }}>Содержимое
            <select style={{ ...FIELD, marginTop: 4 }} value={kind} onChange={event => setKind(event.target.value as Exclude<RowPositionKind, 'rack'>)}>
              <option value="empty">Пустое место / физический разрыв</option>
              <option value="reservation">Резерв</option>
              <option value="inrow_cooler">In-Row Cooler</option>
            </select>
          </label>
          {kind === 'reservation' && <label style={{ fontSize: 11, color: '#94a3b8' }}>Описание резерва
            <input style={{ ...FIELD, marginTop: 4 }} value={label} onChange={event => setLabel(event.target.value)} placeholder="Будущая стойка" />
          </label>}
          {kind === 'inrow_cooler' && <label style={{ fontSize: 11, color: '#94a3b8' }}>Метка оборудования
            <input style={{ ...FIELD, marginTop: 4 }} value={label} onChange={event => setLabel(event.target.value)} placeholder="IR-01" />
          </label>}
          <div style={{ border: '1px solid #1f2937', padding: 10, fontSize: 11, color: '#cbd5e1' }}>
            <div>Позиция: <strong>{position}</strong></div>
            <div>Тип: <strong>{kind === 'empty' ? 'пустое место' : kind === 'reservation' ? 'резерв' : 'In-Row Cooler'}</strong></div>
            {plan.additions[0] && <div style={{ color: '#60a5fa', fontFamily: 'monospace', marginTop: 5 }}>{plan.additions[0].label}</div>}
          </div>
          {plan.errors.length > 0 && <div role="alert" style={{ color: '#fca5a5', background: 'rgba(127,29,29,.2)', border: '1px solid rgba(248,113,113,.35)', padding: 9, fontSize: 11 }}>{plan.errors.map(error => <div key={error}>{error}</div>)}</div>}
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ ...FIELD, width: 'auto', cursor: 'pointer', color: '#cbd5e1' }}>Отмена</button>
          <button onClick={apply} disabled={!plan.canApply} style={{ ...FIELD, width: 'auto', cursor: plan.canApply ? 'pointer' : 'not-allowed', background: plan.canApply ? '#2563eb' : '#1f2937', borderColor: plan.canApply ? '#2563eb' : '#374151', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Применить</button>
        </div>
      </div>
    </div>
  )
}
